import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { SubtaskTree } from './_components/SubtaskTree'
import { CommentThread } from './_components/CommentThread'
import { InlineStatusEdit } from './_components/InlineStatusEdit'
import { InlineAssigneeEdit } from './_components/InlineAssigneeEdit'
import {
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
  type AssigneeOption,
  type TaskDetail,
  type TaskRow,
  type TaskComment,
} from '@/types/work'

interface Props {
  task: TaskDetail
  subtasks: TaskRow[]
  comments: TaskComment[]
  // When `embedded` is true, Show is rendered inside TaskDetailSheet:
  // hide the back-arrow link (the sheet has its own close affordance)
  // and route subtask clicks through `onSelectTask` instead of doing
  // an Inertia navigation that would close the sheet.
  // `onAfterMutation` is called by inline edits + comment mutations so
  // the sheet can re-fetch and stay in sync with the server.
  // `onEnterEdit` is invoked instead of navigating to /edit when set,
  // so the sheet can swap to its in-place edit content.
  // `onCreateSubtask` lets the embedded "Nouvelle sous-tâche" button
  // swap the sheet to its in-place create form (with parent_task_id
  // pre-set). Without it (standalone page), the button is hidden.
  // `assigneeOptions` enables the inline reassign control in the
  // header. Only the embedded sheet flows pass it (the standalone
  // /work/tasks/:id page omits it; reassignment there still routes
  // through the dedicated Edit page if needed).
  // Defaults preserve the existing standalone /work/tasks/:id behavior.
  embedded?: boolean
  assigneeOptions?: AssigneeOption[]
  onSelectTask?: (id: number) => void
  onAfterMutation?: () => void
  onEnterEdit?: () => void
  onCreateSubtask?: () => void
}

const PRIORITY_BADGE: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  low: 'outline',
  medium: 'secondary',
  high: 'default',
  urgent: 'destructive',
}

export default function TasksShow({
  task,
  subtasks,
  comments,
  embedded = false,
  assigneeOptions,
  onSelectTask,
  onAfterMutation,
  onEnterEdit,
  onCreateSubtask,
}: Props) {
  const caps = task.policy_caps

  function handleDestroy() {
    router.delete(`/work/tasks/${task.id}`)
  }

  // Standalone fallback: when not embedded the parent doesn't supply
  // an onAfterMutation, so refresh the current Inertia page after a
  // comment / status change. CommentThread + InlineSelect always go
  // through sheetFetch now, so we own this side-effect here.
  const refreshAfterMutation = onAfterMutation ?? (() => router.reload({ only: [ 'task', 'comments', 'subtasks' ] }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          {!embedded && (
            <Link
              href="/work/tasks"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Tâches
            </Link>
          )}
          <h1 className="text-2xl font-bold tracking-tight">{task.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <InlineStatusEdit
              taskId={task.id}
              value={task.status}
              canUpdate={caps.update_status}
              onAfterChange={refreshAfterMutation}
            />
            <Badge variant={PRIORITY_BADGE[task.priority] ?? 'outline'}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </Badge>
            <Badge variant="outline">{TASK_TYPE_LABELS[task.task_type]}</Badge>
            {/* Three rendering modes:
                  1. Embedded + can reassign — always show the picker
                     (works for both assigned AND unassigned tasks; the
                     current label falls back to "Non assigné" so the
                     control is still discoverable on a fresh task).
                  2. Has an assignee but cannot reassign — plain text.
                  3. Unassigned + cannot reassign — render nothing. */}
            {embedded && assigneeOptions && caps.reassign ? (
              <span className="flex items-center gap-1 text-muted-foreground">
                Assigné à{' '}
                <InlineAssigneeEdit
                  taskId={task.id}
                  value={task.assignee_user_id}
                  options={assigneeOptions}
                  canReassign={caps.reassign}
                  currentLabel={task.assignee_name ?? 'Non assigné'}
                  onAfterChange={refreshAfterMutation}
                />
              </span>
            ) : task.assignee_name ? (
              <span className="text-muted-foreground">
                Assigné à <span className="text-foreground">{task.assignee_name}</span>
              </span>
            ) : null}
          </div>
          {task.subject && (
            <p className="text-sm text-muted-foreground">
              Lié à <span className="font-medium text-foreground">{task.subject.label}</span>
              {task.subject.sublabel && ` · ${task.subject.sublabel}`}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Équipe <span className="font-medium text-foreground">{task.team_name ?? '—'}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Créé par <span className="font-medium text-foreground">{task.created_by_name ?? '—'}</span>
          </p>
        </div>

        <div className="flex gap-2">
          {caps.update && (
            onEnterEdit ? (
              <Button variant="outline" onClick={onEnterEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </Button>
            ) : (
              <Button asChild variant="outline">
                <Link href={`/work/tasks/${task.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Modifier
                </Link>
              </Button>
            )
          )}
          {caps.destroy && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette tâche ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Toutes les sous-tâches et commentaires seront également supprimés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDestroy}>Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Separator />

      {/* Description */}
      {task.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm">{task.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Subtasks */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Sous-tâches ({subtasks.length})</CardTitle>
          {onCreateSubtask && caps.update && (
            <Button size="sm" variant="outline" onClick={onCreateSubtask}>
              <Plus className="mr-1.5 h-4 w-4" />
              Nouvelle sous-tâche
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <SubtaskTree subtasks={subtasks} onSelectTask={onSelectTask} />
        </CardContent>
      </Card>

      {/* Comments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Commentaires ({comments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <CommentThread
            taskId={task.id}
            comments={comments}
            canComment={caps.comment}
            onAfterMutation={refreshAfterMutation}
          />
        </CardContent>
      </Card>
    </div>
  )
}
